import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  InputAdornment,
  IconButton,
  Menu,
  MenuItem,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Badge,
  Tooltip,
  Snackbar,
} from '@mui/material';
import {
  Search,
  MoreVert,
  Add,
  Edit,
  Delete,
  Inventory,
  Visibility,
  VisibilityOff,
  FilterList,
} from '@mui/icons-material';
import { apiService } from '../services/apiService';
import { colors } from '../theme/theme';
import PackFormDialog from '../components/PackFormDialog';
import AdvancedFiltersPopover, { AdvancedFilterSection } from '../components/AdvancedFiltersPopover';
import {
  EMPTY_PACK_ADVANCED_FILTERS,
  PackAdvancedFilters,
  PackScopeFilterValue,
  PackSortFilterValue,
  PackStatusFilterValue,
  PackStudioOption,
} from '../components/advancedFilterTypes';
import { isOwnerRole } from '../utils/roles';
import { useMyLocations } from '../hooks/useMyLocations';
import BranchChip from '../components/BranchChip';

interface Capability {
  title: string;
  image: string;
}

interface Pack {
  _id: string;
  title: string;
  image: string;
  description: string;
  capabilities: Capability[];
  totalClasses?: number;
  totalPrice?: number;
  pricePerClass?: number;
  isActive: boolean;
  isPrivate?: boolean;
  allowGuestBooking?: boolean;
  createdAt: string;
  updatedAt: string;
  studio?: any;
  category?: { _id?: string; title?: string; image?: string } | string | null;
  // Shared pack: totalClasses usable across these categories / classes (populated).
  allowedCategories?: Array<{ _id?: string; title?: string } | string>;
  allowedClasses?: Array<{ _id?: string; className?: string } | string>;
  sessionType?: string;
  purchaseCount?: number;
  maxPurchasesPerUser?: number | null;
  freezeDays?: number | null;
  freezeAllowed?: boolean;
}

/** "Yoga · Pilates" for a shared pack , or null for any other pack. */
const sharedScopeLabel = (pack: Pack): string | null => {
  const names = (pack.allowedCategories?.length ? pack.allowedCategories : pack.allowedClasses ?? [])
    .map((x: any) => (typeof x === 'object' ? x?.title ?? x?.className : null))
    .filter(Boolean);
  if (!pack.allowedCategories?.length && !pack.allowedClasses?.length) return null;
  return names.length ? names.join(' · ') : 'Several';
};

function parseSingleParam<T extends string>(raw: string | null, allowed: T[]): T | null {
  if (!raw) return null;
  const value = raw.split(',')[0];
  return allowed.includes(value as T) ? (value as T) : null;
}

function parseFiltersFromParams(params: URLSearchParams): PackAdvancedFilters {
  return {
    scope: parseSingleParam(params.get('scope') || params.get('type'), ['global', 'studio_specific']),
    studioIds: (params.get('studios') || '').split(',').filter(Boolean),
    status: parseSingleParam(params.get('status'), ['active', 'inactive']),
    sort: parseSingleParam(params.get('sort'), [
      'price_asc',
      'price_desc',
      'most_purchased',
      'least_purchased',
      'created_newest',
      'created_oldest',
    ]),
  };
}

function filtersToParams(filters: PackAdvancedFilters, searchTerm: string): URLSearchParams {
  const params = new URLSearchParams();
  if (searchTerm.trim()) params.set('q', searchTerm.trim());
  if (filters.scope) params.set('scope', filters.scope);
  if (filters.studioIds.length > 0) params.set('studios', filters.studioIds.join(','));
  if (filters.status) params.set('status', filters.status);
  if (filters.sort) params.set('sort', filters.sort);
  return params;
}

function countActiveFilters(filters: PackAdvancedFilters): number {
  return (
    (filters.scope ? 1 : 0) +
    (filters.status ? 1 : 0) +
    (filters.sort ? 1 : 0)
  );
}

function getPackStudioId(pack: Pack): string | null {
  if (!pack.studio) return null;
  return typeof pack.studio === 'string' ? pack.studio : pack.studio._id;
}

function getPackPrice(pack: Pack): number {
  return pack.totalPrice ?? pack.pricePerClass ?? 0;
}

function getPackPurchaseCount(pack: Pack, purchaseCounts: Record<string, number>): number {
  if (typeof pack.purchaseCount === 'number') return pack.purchaseCount;
  return purchaseCounts[pack._id] ?? 0;
}

function deriveStudiosFromPacks(packList: Pack[]): PackStudioOption[] {
  const map = new Map<string, PackStudioOption>();
  packList.forEach(pack => {
    const studioId = getPackStudioId(pack);
    if (!studioId) return;
    const studio = pack.studio;
    map.set(studioId, {
      _id: studioId,
      studioName:
        typeof studio === 'object' && studio?.studioName
          ? studio.studioName
          : 'Unknown Studio',
      city: typeof studio === 'object' ? studio?.city : undefined,
    });
  });
  return Array.from(map.values()).sort((a, b) => a.studioName.localeCompare(b.studioName));
}

const SESSION_TYPE_ORDER: Record<string, number> = { group: 0, private: 1, duet: 2 };
const sessionTypeOrder = (t?: string) => SESSION_TYPE_ORDER[t ?? 'group'] ?? 0;

const PacksPage: React.FC = () => {
  // Branch identity per row while "All locations" aggregates them.
  const { byId: locationById, isAllLocations } = useMyLocations();

  const { admin } = useAuth();
  const isStudioRole = isOwnerRole(admin?.role) || admin?.role === 'manager';
  const [searchParams, setSearchParams] = useSearchParams();
  const [packs, setPacks] = useState<Pack[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const searchTerm = searchParams.get('q') ?? '';
  const [searchInput, setSearchInput] = useState(searchTerm);
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const skipSearchSyncRef = useRef(false);
  const appliedFilters = useMemo(() => parseFiltersFromParams(searchParams), [searchParams]);
  const activeFilterCount = countActiveFilters(appliedFilters);
  const [filterAnchorEl, setFilterAnchorEl] = useState<HTMLElement | null>(null);
  const [draftFilters, setDraftFilters] = useState<PackAdvancedFilters>(EMPTY_PACK_ADVANCED_FILTERS);
  const [purchaseCounts, setPurchaseCounts] = useState<Record<string, number>>({});
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [packNote, setPackNote] = useState<string | null>(null);
  const [selectedPack, setSelectedPack] = useState<Pack | null>(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [toggling, setToggling] = useState(false);

  const fetchPacks = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const params: Record<string, string | boolean> = {};
      if (searchTerm) params.search = searchTerm;

      if (appliedFilters.status === 'active') {
        params.isActive = true;
      } else       if (appliedFilters.status === 'inactive') {
        params.isActive = false;
      }

      const response = await apiService.getPacks(params);
      const packsList =
        response.packs ||
        response.studioPacks ||
        response.data?.packs ||
        response.data?.studioPacks ||
        response.data ||
        [];
      setPacks(Array.isArray(packsList) ? packsList : []);
    } catch (err: any) {
      console.error('Error fetching packs:', err);
      setError(err.message || 'Failed to fetch packs');
      setPacks([]);
    } finally {
      setIsLoading(false);
    }
  }, [searchTerm, appliedFilters.status]);

  useEffect(() => {
    fetchPacks();
  }, [fetchPacks]);

  const studiosWithPacks = useMemo(() => deriveStudiosFromPacks(packs), [packs]);

  useEffect(() => {
    if (isStudioRole) return;

    const loadPurchaseCounts = async () => {
      try {
        const response = await apiService.getPurchasedPacks({ page: 1, pageSize: 10000 });
        const purchases = response.purchasedPacks || response.data?.purchasedPacks || [];
        const counts: Record<string, number> = {};
        (Array.isArray(purchases) ? purchases : []).forEach((pp: any) => {
          const packId = typeof pp.pack === 'string' ? pp.pack : pp.pack?._id;
          if (packId) counts[packId] = (counts[packId] || 0) + 1;
        });
        setPurchaseCounts(counts);
      } catch (err) {
        console.error('Error fetching pack purchase counts:', err);
      }
    };

    loadPurchaseCounts();
  }, [isStudioRole]);

  useEffect(() => {
    if (skipSearchSyncRef.current) {
      skipSearchSyncRef.current = false;
      return;
    }
    setSearchInput(searchTerm);
  }, [searchTerm]);

  useEffect(() => {
    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
  }, []);

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearchInput(value);
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      skipSearchSyncRef.current = true;
      setSearchParams(prev => filtersToParams(parseFiltersFromParams(prev), value));
    }, 400);
  };

  const openFilterPanel = (event: React.MouseEvent<HTMLElement>) => {
    setDraftFilters(appliedFilters);
    setFilterAnchorEl(event.currentTarget);
  };

  const closeFilterPanel = () => {
    setFilterAnchorEl(null);
  };

  const applyFilters = () => {
    skipSearchSyncRef.current = true;
    setSearchParams(filtersToParams(draftFilters, searchInput));
    closeFilterPanel();
  };

  const clearDraftFilters = () => {
    setDraftFilters(EMPTY_PACK_ADVANCED_FILTERS);
  };

  const clearAllFilters = () => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    setSearchInput('');
    skipSearchSyncRef.current = true;
    setSearchParams(new URLSearchParams());
    setDraftFilters(EMPTY_PACK_ADVANCED_FILTERS);
    closeFilterPanel();
  };

  const packFilterSections = useMemo((): AdvancedFilterSection[] => {
    const sections: AdvancedFilterSection[] = [];

    if (!isStudioRole) {
      sections.push({
        type: 'single-select-toggle',
        label: 'Pack Type',
        options: [
          { value: 'global', label: 'Global' },
          { value: 'studio_specific', label: 'Studio-specific' },
        ],
        value: draftFilters.scope,
        onToggle: value => {
          setDraftFilters(prev => {
            if (prev.scope === value) {
              return { ...prev, scope: null, studioIds: [] };
            }
            return {
              ...prev,
              scope: value as PackScopeFilterValue,
              studioIds: value === 'global' ? [] : prev.studioIds,
            };
          });
        },
      });

      if (draftFilters.scope === 'studio_specific') {
        sections.push({
          type: 'searchable-multi-select',
          label: 'Studio',
          options: studiosWithPacks,
          selectedIds: draftFilters.studioIds,
          onChange: studioIds => setDraftFilters(prev => ({ ...prev, studioIds })),
        });
      }
    }

    sections.push(
      {
        type: 'single-select-toggle',
        label: 'Status',
        options: [
          { value: 'active', label: 'Active' },
          { value: 'inactive', label: 'Inactive' },
        ],
        value: draftFilters.status,
        onToggle: value => {
          setDraftFilters(prev => ({
            ...prev,
            status: prev.status === value ? null : (value as PackStatusFilterValue),
          }));
        },
      },
      {
        type: 'single-select-toggle',
        label: 'Sort',
        options: [],
        value: draftFilters.sort,
        onToggle: value => {
          setDraftFilters(prev => ({
            ...prev,
            sort: prev.sort === value ? null : (value as PackSortFilterValue),
          }));
        },
        rows: [
          [
            { value: 'price_asc', label: 'Price Low → High' },
            { value: 'price_desc', label: 'Price High → Low' },
          ],
          [
            { value: 'most_purchased', label: 'Most Purchased' },
            { value: 'least_purchased', label: 'Least Purchased' },
          ],
          [
            { value: 'created_newest', label: 'Newest' },
            { value: 'created_oldest', label: 'Oldest' },
          ],
        ],
      },
    );

    return sections;
  }, [draftFilters, isStudioRole, studiosWithPacks]);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, pack: Pack) => {
    setAnchorEl(event.currentTarget);
    setSelectedPack(pack);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleEditClick = () => {
    setAnchorEl(null);
    setEditDialogOpen(true);
  };

  const handleDeleteClick = () => {
    setDeleteError(null);
    setDeleteDialogOpen(true);
    setAnchorEl(null);
  };

  const handleDelete = async () => {
    if (!selectedPack) return;
    try {
      setDeleting(true);
      setDeleteError(null);
      await apiService.deletePack(selectedPack._id, getPackStudioId(selectedPack));
      await fetchPacks();
      setDeleteDialogOpen(false);
      setSelectedPack(null);
    } catch (err: any) {
      setDeleteError(err.message || 'Failed to delete pack');
    } finally {
      setDeleting(false);
    }
  };

  const handleToggleStatus = async (pack: Pack) => {
    try {
      setToggling(true);
      await apiService.togglePackStatus(pack._id, !pack.isActive, getPackStudioId(pack));
      await fetchPacks();
    } catch (err: any) {
      setError(err.message || 'Failed to update pack status');
    } finally {
      setToggling(false);
    }
  };

  const hasActiveFilters =
    activeFilterCount > 0 || Boolean(searchTerm.trim());

  const filteredPacks = useMemo(() => {
    let result = packs.filter(pack => {
      if (appliedFilters.scope === 'global') {
        if (getPackStudioId(pack)) return false;
      }
      if (appliedFilters.scope === 'studio_specific') {
        const studioId = getPackStudioId(pack);
        if (!studioId) return false;
        if (
          appliedFilters.studioIds.length > 0 &&
          !appliedFilters.studioIds.includes(studioId)
        ) {
          return false;
        }
      }
      return true;
    });

    const tieBreak = (a: Pack, b: Pack) =>
      sessionTypeOrder(a.sessionType) - sessionTypeOrder(b.sessionType);

    if (appliedFilters.sort === 'price_asc') {
      result = [...result].sort((a, b) => {
        const diff = getPackPrice(a) - getPackPrice(b);
        return diff !== 0 ? diff : tieBreak(a, b);
      });
    } else if (appliedFilters.sort === 'price_desc') {
      result = [...result].sort((a, b) => {
        const diff = getPackPrice(b) - getPackPrice(a);
        return diff !== 0 ? diff : tieBreak(a, b);
      });
    } else if (appliedFilters.sort === 'most_purchased') {
      result = [...result].sort((a, b) => {
        const diff =
          getPackPurchaseCount(b, purchaseCounts) - getPackPurchaseCount(a, purchaseCounts);
        return diff !== 0 ? diff : tieBreak(a, b);
      });
    } else if (appliedFilters.sort === 'least_purchased') {
      result = [...result].sort((a, b) => {
        const diff =
          getPackPurchaseCount(a, purchaseCounts) - getPackPurchaseCount(b, purchaseCounts);
        return diff !== 0 ? diff : tieBreak(a, b);
      });
    } else if (appliedFilters.sort === 'created_newest') {
      result = [...result].sort(
        (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
      );
    } else if (appliedFilters.sort === 'created_oldest') {
      result = [...result].sort(
        (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
      );
    } else {
      result = [...result].sort(tieBreak);
    }

    return result;
  }, [packs, appliedFilters, purchaseCounts]);

  if (isLoading && packs.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" fontWeight={700} color={colors.textPrimary}>
          Packs
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => setCreateDialogOpen(true)}
          sx={{
            backgroundColor: colors.primary,
            '&:hover': {
              backgroundColor: colors.primaryDark,
            },
          }}
        >
          Create Pack
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Search and Filters */}
      <Box mb={3} display="flex" gap={2} flexWrap="wrap" alignItems="center">
        <TextField
          variant="outlined"
          placeholder="Search packs..."
          value={searchInput}
          onChange={handleSearch}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
          sx={{ flex: 1, minWidth: 250 }}
        />
        <Badge
          badgeContent={activeFilterCount}
          color="primary"
          invisible={activeFilterCount === 0}
          overlap="circular"
        >
          <Button
            variant="outlined"
            startIcon={<FilterList />}
            onClick={openFilterPanel}
            sx={{
              minWidth: 120,
              fontWeight: 600,
              textTransform: 'none',
            }}
          >
            Filters
          </Button>
        </Badge>
      </Box>

      {hasActiveFilters && (
        <Typography variant="body2" color="text.secondary" mb={2}>
          {filteredPacks.length} of {packs.length} packs
          {' · '}
          <Button
            variant="text"
            size="small"
            onClick={clearAllFilters}
            sx={{ textTransform: 'none', p: 0, minWidth: 0, verticalAlign: 'baseline' }}
          >
            Clear filters
          </Button>
        </Typography>
      )}

      <AdvancedFiltersPopover
        open={Boolean(filterAnchorEl)}
        anchorEl={filterAnchorEl}
        onClose={closeFilterPanel}
        sections={packFilterSections}
        onApply={applyFilters}
        onClearAll={clearDraftFilters}
        width={360}
      />

      <Card sx={{ border: `1px solid ${colors.divider}` }}>
        <CardContent>
          {isLoading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress size={28} />
            </Box>
          ) : filteredPacks.length === 0 ? (
            <Box textAlign="center" py={4}>
              <Inventory sx={{ fontSize: 64, color: colors.textSecondary, mb: 2 }} />
              <Typography variant="h6" color={colors.textSecondary}>
                {hasActiveFilters ? 'No packs match your filters' : 'No packs yet'}
              </Typography>
              <Typography variant="body2" color={colors.textSecondary} mt={1}>
                {hasActiveFilters
                  ? 'Try adjusting your filters or search terms'
                  : 'Create your first pack to get started'}
              </Typography>
              {hasActiveFilters && (
                <Button variant="contained" onClick={clearAllFilters} sx={{ mt: 2, textTransform: 'none' }}>
                  Clear filters
                </Button>
              )}
            </Box>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: colors.background }}>
                    <TableCell sx={{ fontWeight: 600 }}>Image</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Title</TableCell>
                    {!isStudioRole && <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>}
                    <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Total Classes</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Total Price</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Capabilities</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Created</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredPacks.map((pack) => (
                    <TableRow key={pack._id} hover>
                      <TableCell>
                        {pack.image ? (
                          <Box
                            component="img"
                            src={pack.image}
                            alt={pack.title}
                            onError={(e: any) => {
                              e.target.style.display = 'none';
                            }}
                            sx={{
                              width: 60,
                              height: 60,
                              objectFit: 'cover',
                              borderRadius: 1,
                              border: `1px solid ${colors.divider}`,
                            }}
                          />
                        ) : (
                          <Box
                            sx={{
                              width: 60,
                              height: 60,
                              backgroundColor: colors.background,
                              borderRadius: 1,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              border: `1px solid ${colors.divider}`,
                            }}
                          >
                            <Inventory sx={{ fontSize: 24, color: colors.textSecondary }} />
                          </Box>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body1" fontWeight={600}>
                          {pack.title}
                        </Typography>
                        {pack.sessionType === 'private' && (
                          <Chip label="Private" size="small" sx={{ mt: 0.5, backgroundColor: '#6A1B9A20', color: '#6A1B9A', fontWeight: 600, fontSize: '0.7rem' }} />
                        )}
                        {pack.sessionType === 'duet' && (
                          <Chip label="Duet" size="small" sx={{ mt: 0.5, backgroundColor: '#00838F20', color: '#00838F', fontWeight: 600, fontSize: '0.7rem' }} />
                        )}
                        {/* Which branch this pack belongs to. The Studio
                            column beside it is superadmin-only, so without
                            this a brand owner sees two branches' identically
                            named packs with nothing to tell them apart. */}
                        {isAllLocations && (
                          <Box sx={{ mt: 0.5 }}>
                            <BranchChip location={locationById(getPackStudioId(pack) || undefined)} />
                          </Box>
                        )}
                        {sharedScopeLabel(pack) && (
                          <Tooltip title={`Shared pack — ${pack.totalClasses ?? ''} classes usable across ${sharedScopeLabel(pack)} in any mix`}>
                            <Chip label={`Shared: ${sharedScopeLabel(pack)}`} size="small" sx={{ mt: 0.5, ml: 0.5, backgroundColor: '#EEF2FF', color: '#3949AB', fontWeight: 600, fontSize: '0.7rem', maxWidth: 260 }} />
                          </Tooltip>
                        )}
                        {Number(pack.freezeDays) >= 1 && pack.freezeAllowed !== false && (
                          <Tooltip title="Members get this many freeze days in total for the life of the pack, instead of the studio freeze policy.">
                            <Chip
                              label={`Freeze: ${pack.freezeDays} days`}
                              size="small"
                              sx={{ mt: 0.5, ml: 0.5, backgroundColor: '#E1F5FE', color: '#01579B', fontWeight: 600, fontSize: '0.7rem' }}
                            />
                          </Tooltip>
                        )}
                        {Number(pack.maxPurchasesPerUser) >= 1 && (
                          <Tooltip title="Each member can buy this pack only this many times. Members at the limit no longer see it in the app.">
                            <Chip
                              label={pack.maxPurchasesPerUser === 1 ? 'Once per member' : `Max ${pack.maxPurchasesPerUser} / member`}
                              size="small"
                              sx={{ mt: 0.5, ml: 0.5, backgroundColor: '#FFF3E0', color: '#E65100', fontWeight: 600, fontSize: '0.7rem' }}
                            />
                          </Tooltip>
                        )}
                      </TableCell>
                      {!isStudioRole && (
                        <TableCell>
                          {pack.studio ? (
                            <Chip
                              label={`Studio: ${pack.studio?.studioName || 'N/A'}`}
                              size="small"
                              sx={{ backgroundColor: colors.info + '20', color: colors.info, fontWeight: 600 }}
                            />
                          ) : (
                            <Chip
                              label="SweatSocial"
                              size="small"
                              sx={{ backgroundColor: colors.primary + '20', color: colors.primary, fontWeight: 600 }}
                            />
                          )}
                        </TableCell>
                      )}
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{
                            maxWidth: 300,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {pack.description}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {pack.totalClasses ? pack.totalClasses : 'Unlimited'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          ₹{pack.totalPrice || pack.pricePerClass || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box display="flex" gap={0.5} flexWrap="wrap">
                          {pack.capabilities && pack.capabilities.length > 0 ? (
                            pack.capabilities.slice(0, 2).map((cap, idx) => (
                              <Chip
                                key={idx}
                                label={cap.title}
                                size="small"
                                sx={{
                                  backgroundColor: `${colors.primary}15`,
                                  color: colors.primary,
                                  fontSize: '0.7rem',
                                }}
                              />
                            ))
                          ) : (
                            <Typography variant="caption" color="text.secondary">
                              None
                            </Typography>
                          )}
                          {pack.capabilities && pack.capabilities.length > 2 && (
                            <Chip
                              label={`+${pack.capabilities.length - 2}`}
                              size="small"
                              sx={{
                                backgroundColor: `${colors.textSecondary}15`,
                                color: colors.textSecondary,
                                fontSize: '0.7rem',
                              }}
                            />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                          <Chip
                            label={pack.isActive ? 'Active' : 'Inactive'}
                            size="small"
                            sx={{
                              backgroundColor: pack.isActive
                                ? `${colors.success}15`
                                : `${colors.error}15`,
                              color: pack.isActive
                                ? colors.success
                                : colors.error,
                              fontWeight: 600,
                            }}
                          />
                          {pack.isPrivate && (
                            <Chip
                              label="Private"
                              size="small"
                              sx={{
                                backgroundColor: `${colors.warning}15`,
                                color: colors.warning,
                                fontWeight: 600,
                              }}
                            />
                          )}
                          {pack.allowGuestBooking && (
                            <Chip
                              label="Guests"
                              size="small"
                              sx={{
                                backgroundColor: '#00838F20',
                                color: '#00838F',
                                fontWeight: 600,
                              }}
                            />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {new Date(pack.createdAt).toLocaleDateString()}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          onClick={(e) => handleMenuOpen(e, pack)}
                        >
                          <MoreVert />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem
          onClick={(e) => {
            e.stopPropagation();
            handleEditClick();
          }}
        >
          <Edit sx={{ mr: 1, fontSize: 20 }} />
          Edit
        </MenuItem>
        <MenuItem
          onClick={(e) => {
            e.stopPropagation();
            if (selectedPack) {
              handleToggleStatus(selectedPack);
            }
          }}
          disabled={toggling}
        >
          {selectedPack?.isActive ? (
            <>
              <VisibilityOff sx={{ mr: 1, fontSize: 20 }} />
              Deactivate
            </>
          ) : (
            <>
              <Visibility sx={{ mr: 1, fontSize: 20 }} />
              Activate
            </>
          )}
        </MenuItem>
        <MenuItem
          onClick={(e) => {
            e.stopPropagation();
            handleDeleteClick();
          }}
          sx={{ color: colors.error }}
        >
          <Delete sx={{ mr: 1, fontSize: 20 }} />
          Delete
        </MenuItem>
      </Menu>

      <PackFormDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onSuccess={() => {
          fetchPacks();
          setCreateDialogOpen(false);
        }}
      />

      {selectedPack && (
        <>
          <PackFormDialog
            open={editDialogOpen}
            onClose={() => {
              setEditDialogOpen(false);
              setSelectedPack(null);
            }}
            onSuccess={(note) => {
              fetchPacks();
              setEditDialogOpen(false);
              setSelectedPack(null);
              if (note) setPackNote(note);
            }}
            pack={selectedPack}
          />

          <Box
            sx={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              display: deleteDialogOpen ? 'flex' : 'none',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1300,
            }}
            onClick={() => { setDeleteDialogOpen(false); setDeleteError(null); }}
          >
            <Card
              sx={{ p: 3, maxWidth: 400, width: '90%' }}
              onClick={(e) => e.stopPropagation()}
            >
              <Typography variant="h6" fontWeight={600} mb={2}>
                Delete Pack
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={3}>
                Are you sure you want to delete the pack "{selectedPack.title}"? This action cannot be undone.
              </Typography>
              {deleteError && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setDeleteError(null)}>
                  {deleteError}
                </Alert>
              )}
              <Box display="flex" gap={2} justifyContent="flex-end">
                <Button
                  variant="outlined"
                  onClick={() => { setDeleteDialogOpen(false); setDeleteError(null); }}
                  disabled={deleting}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  color="error"
                  onClick={handleDelete}
                  disabled={deleting}
                  startIcon={deleting && <CircularProgress size={16} />}
                >
                  Delete
                </Button>
              </Box>
            </Card>
          </Box>
        </>
      )}
      <Snackbar
        open={!!packNote}
        autoHideDuration={6000}
        onClose={() => setPackNote(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => setPackNote(null)} sx={{ width: '100%' }}>
          {packNote}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PacksPage;
